import type {
  UIStateSettings,
  Helper,
  ToolMode,
  ToolName,
  SubtoolName,
  Theme,
  Language,
} from "@/types";
import type { DominantHand } from "@/types/user-types";
import { getSubtoolsForMode } from "@/data/tool-mode-map";
import type { TrackedPrefs } from "./persist";

const HELPER_KEYS: Helper[] = [
  "rulers",
  "snap",
  "guides",
  "dots",
  "lines",
  "overlay",
];
const HELPER_SET = new Set<string>(HELPER_KEYS);

type ScreenStateLike = Record<string, unknown> | null | undefined;

type NullableStagePosition = { x: number; y: number } | null | undefined;

const isObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object";

const parseStagePosition = (value: NullableStagePosition): { x: number; y: number } | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const maybe = value as Record<string, unknown>;
  const x = maybe.x;
  const y = maybe.y;
  if (typeof x === "number" && typeof y === "number") {
    return { x, y };
  }
  return undefined;
};

const helpersObjectToArray = (value: unknown): Helper[] | undefined => {
  if (Array.isArray(value)) {
    const arr = value
      .filter((item): item is string => typeof item === "string")
      .filter((item, idx, self) => self.indexOf(item) === idx && HELPER_SET.has(item));
    return arr as Helper[];
  }
  if (isObject(value)) {
    const arr: Helper[] = [];
    for (const key of Object.keys(value)) {
      if (!HELPER_SET.has(key)) continue;
      if ((value as Record<string, unknown>)[key]) {
        arr.push(key as Helper);
      }
    }
    return arr;
  }
  return undefined;
};

const helpersObjectToFlags = (value: unknown): TrackedPrefs["helpers"] | undefined => {
  if (isObject(value)) {
    const flags: NonNullable<TrackedPrefs["helpers"]> = {};
    let hasAny = false;
    for (const key of HELPER_KEYS) {
      const v = (value as Record<string, unknown>)[key];
      if (typeof v === "boolean") {
        flags[key] = v;
        if (v) hasAny = true;
      }
    }
    if (Object.keys(flags).length) {
      if (!hasAny) {
        // Preserve explicit false map so we can clear helpers server-side
        return flags;
      }
      return flags;
    }
  }
  if (Array.isArray(value)) {
    const flags: NonNullable<TrackedPrefs["helpers"]> = {};
    for (const helper of value) {
      if (typeof helper !== "string") continue;
      if (HELPER_SET.has(helper)) {
        flags[helper as Helper] = true;
      }
    }
    return Object.keys(flags).length ? flags : undefined;
  }
  return undefined;
};

const coerceToolNameFromMode = (mode: ToolMode | undefined): ToolName | undefined => {
  switch (mode) {
    case "sketch":
      return "draw";
    case "edit":
      return "select";
    default:
      return undefined;
  }
};

const coerceTheme = (value: unknown): Theme | undefined =>
  value === "light" || value === "dark" ? (value as Theme) : undefined;

const coerceLanguage = (value: unknown): Language | undefined =>
  typeof value === "string" ? (value as Language) : undefined;

const coerceBreakpoint = (value: unknown): UIStateSettings["current_breakpoint"] | undefined =>
  typeof value === "string" ? (value as UIStateSettings["current_breakpoint"]) : undefined;

const coerceZoomMode = (value: unknown): UIStateSettings["zoom_mode"] | undefined =>
  typeof value === "string" ? (value as UIStateSettings["zoom_mode"]) : undefined;

const coerceToolMode = (value: unknown): ToolMode | undefined =>
  typeof value === "string" ? (value as ToolMode) : undefined;

const coerceSubtool = (value: unknown): SubtoolName | undefined =>
  typeof value === "string" ? (value as SubtoolName) : undefined;

const coerceHandedness = (value: unknown): DominantHand | undefined =>
  value === "left" || value === "right" ? (value as DominantHand) : undefined;

const coerceShowAll = (
  value: unknown
): Partial<UIStateSettings["show_all"]> | undefined => {
  if (!isObject(value)) return undefined;
  const editRaw = (value as Record<string, unknown>).edit;
  const sketchRaw = (value as Record<string, unknown>).sketch;
  const next: Partial<UIStateSettings["show_all"]> = {};
  if (typeof editRaw === "boolean") next.edit = editRaw;
  if (typeof sketchRaw === "boolean") next.sketch = sketchRaw;
  return Object.keys(next).length ? next : undefined;
};

export const extractUiStatePatchFromScreen = (
  screen: ScreenStateLike
): Partial<UIStateSettings> => {
  if (!screen || !isObject(screen)) return {};
  const patch: Partial<UIStateSettings> = {};

  const theme = coerceTheme(screen.theme);
  if (theme) patch.theme = theme;

  const language = coerceLanguage((screen as Record<string, unknown>).currentLanguage);
  if (language) patch.current_language = language;

  const breakpoint = coerceBreakpoint((screen as Record<string, unknown>).currentBreakpoint);
  if (breakpoint) patch.current_breakpoint = breakpoint;

  const stagePosition = parseStagePosition((screen as Record<string, unknown>).stagePosition as NullableStagePosition);
  if (stagePosition) patch.stage_position = stagePosition;

  const zoomMode = coerceZoomMode((screen as Record<string, unknown>).zoomMode);
  if (zoomMode) patch.zoom_mode = zoomMode;

  const zoomValue = (screen as Record<string, unknown>).zoomValue;
  if (typeof zoomValue === "number") patch.zoom_value = zoomValue;

  const helpers = helpersObjectToArray((screen as Record<string, unknown>).helpers);
  if (helpers) patch.helpers = helpers;

  const audioMuted = (screen as Record<string, unknown>).audioMuted;
  if (typeof audioMuted === "boolean") patch.audio_muted = audioMuted;

  const showAll = coerceShowAll(
    (screen as Record<string, unknown>).showAll ??
      (screen as Record<string, unknown>).show_all
  );
  if (showAll) {
    patch.show_all = showAll as any;
  }

  const currentPage = (screen as Record<string, unknown>).currentPage;
  if (typeof currentPage === "string") patch.current_page = currentPage;

  const selectedMode = coerceToolMode((screen as Record<string, unknown>).selectedMode);
  if (selectedMode) patch.selectedMode = selectedMode;

  const sketchEraserMode =
    (screen as Record<string, unknown>).sketchEraserMode ??
    (screen as Record<string, unknown>).sketch_eraser_mode;
  if (sketchEraserMode === "delete" || sketchEraserMode === "delete-clean") {
    (patch as any).sketchEraserMode = sketchEraserMode;
  }

  const sketchSelectionMode =
    (screen as Record<string, unknown>).sketchSelectionMode ??
    (screen as Record<string, unknown>).sketch_selection_mode;
  if (sketchSelectionMode === "marquee" || sketchSelectionMode === "lasso") {
    (patch as any).sketchSelectionMode = sketchSelectionMode;
  }

  const serverSelectedTool = coerceSubtool((screen as Record<string, unknown>).selectedTool);
  const allowServerSubtool =
    !!serverSelectedTool &&
    (!selectedMode ||
      getSubtoolsForMode(selectedMode).includes(serverSelectedTool as SubtoolName));
  if (allowServerSubtool) {
    patch.selectedTool = serverSelectedTool;
    patch.selected_subtool = serverSelectedTool;
  }

  const legacySelectedTool = coerceSubtool((screen as Record<string, unknown>).selected_subtool);
  const allowLegacySubtool =
    !!legacySelectedTool &&
    (!selectedMode ||
      getSubtoolsForMode(selectedMode).includes(legacySelectedTool as SubtoolName));
  if (!patch.selectedTool && allowLegacySubtool) {
    patch.selectedTool = legacySelectedTool;
    patch.selected_subtool = legacySelectedTool;
  }

  const selectedToolName = ((): ToolName | undefined => {
    const explicit = (screen as Record<string, unknown>).selected_tool;
    if (typeof explicit === "string") return explicit as ToolName;
    return coerceToolNameFromMode(selectedMode);
  })();
  if (selectedToolName) patch.selected_tool = selectedToolName;

  return patch;
};

export const extractPersistedPrefsFromScreen = (
  screen: ScreenStateLike
): TrackedPrefs => {
  if (!screen || !isObject(screen)) return {};

  const result: TrackedPrefs = {};

  const theme = coerceTheme(screen.theme);
  if (theme) result.theme = theme;

  const language = coerceLanguage((screen as Record<string, unknown>).currentLanguage);
  if (language) result.currentLanguage = language;

  const breakpoint = coerceBreakpoint((screen as Record<string, unknown>).currentBreakpoint);
  if (breakpoint) result.currentBreakpoint = breakpoint;

  const stagePosition = parseStagePosition((screen as Record<string, unknown>).stagePosition as NullableStagePosition);
  if (stagePosition) result.stagePosition = stagePosition;

  const zoomMode = (screen as Record<string, unknown>).zoomMode;
  if (typeof zoomMode === "string") result.zoomMode = zoomMode;

  const zoomValue = (screen as Record<string, unknown>).zoomValue;
  if (typeof zoomValue === "number") result.zoomValue = zoomValue;

  const helpers = helpersObjectToFlags((screen as Record<string, unknown>).helpers);
  if (helpers) result.helpers = helpers;

  const audioMuted = (screen as Record<string, unknown>).audioMuted;
  if (typeof audioMuted === "boolean") result.audioMuted = audioMuted;

  const showAll = coerceShowAll(
    (screen as Record<string, unknown>).showAll ??
      (screen as Record<string, unknown>).show_all
  );
  if (showAll) result.showAll = showAll;

  const selectedMode = (screen as Record<string, unknown>).selectedMode;
  if (typeof selectedMode === "string") result.selectedMode = selectedMode;

  const selectedTool = (screen as Record<string, unknown>).selectedTool;
  if (typeof selectedTool === "string") result.selectedTool = selectedTool;

  const sketchEraserMode =
    (screen as Record<string, unknown>).sketchEraserMode ??
    (screen as Record<string, unknown>).sketch_eraser_mode;
  if (sketchEraserMode === "delete" || sketchEraserMode === "delete-clean") {
    result.sketchEraserMode = sketchEraserMode;
  }

  const sketchSelectionMode =
    (screen as Record<string, unknown>).sketchSelectionMode ??
    (screen as Record<string, unknown>).sketch_selection_mode;
  if (sketchSelectionMode === "marquee" || sketchSelectionMode === "lasso") {
    result.sketchSelectionMode = sketchSelectionMode;
  }

  const handedness = coerceHandedness(
    (screen as Record<string, unknown>).handedness ??
      (screen as Record<string, unknown>).dominantHand
  );
  if (handedness) result.handedness = handedness;

  return result;
};

export const resolveProjectIdFromSessionNode = (
  projectNode: Record<string, unknown> | null | undefined,
  screen: ScreenStateLike
): string | null => {
  if (projectNode && typeof projectNode.projectId === "string") {
    return projectNode.projectId as string;
  }
  const nestedProject = projectNode?.project;
  if (nestedProject && typeof (nestedProject as Record<string, unknown>).id === "string") {
    return ((nestedProject as Record<string, unknown>).id as string) ?? null;
  }
  if (screen && isObject(screen) && typeof screen.projectId === "string") {
    return screen.projectId as string;
  }
  return null;
};
